import { useState, useEffect, useCallback } from "react";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, X, RefreshCw,
  ChevronDown, ChevronRight, Sparkles, BookOpen, Layers
} from "lucide-react";
import api from "../services/api";

// ── Helpers ───────────────────────────────────────────────────────────────────

function stripHtml(html) {
  return (html || "").replace(/<[^>]+>/g, "").trim();
}

const ISSUE_TYPE_LABELS = {
  spelling: "Yazım Hatası",
  punctuation: "Noktalama Hatası",
  clarity: "Netlik Sorunu",
  other: "Diğer",
};

const ISSUE_TYPE_COLORS = {
  spelling: "bg-red-100 text-red-700",
  punctuation: "bg-amber-100 text-amber-700",
  clarity: "bg-violet-100 text-violet-700",
  other: "bg-slate-100 text-slate-600",
};

// ── Suggestion Card ───────────────────────────────────────────────────────────

function SuggestionCard({ suggestion, onApply, onDismiss, applying, dismissing }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="border border-amber-200 rounded-xl bg-white overflow-hidden"
      data-testid="suggestion-card"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-amber-50/60 border-b border-amber-100">
        <AlertTriangle size={15} className="text-amber-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-xs font-bold text-slate-700">{suggestion.question_code}</span>
          <span className="mx-2 text-slate-300">·</span>
          <span className="text-xs text-slate-500">{suggestion.lesson_name}</span>
          {suggestion.topic && (
            <><span className="mx-1 text-slate-300">›</span>
              <span className="text-xs text-slate-400 truncate">{suggestion.topic}</span></>
          )}
        </div>
        {/* Issue type badges */}
        <div className="hidden sm:flex items-center gap-1 shrink-0">
          {[...new Set((suggestion.issues || []).map(i => i.type))].map(type => (
            <span key={type} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ISSUE_TYPE_COLORS[type] || ISSUE_TYPE_COLORS.other}`}>
              {ISSUE_TYPE_LABELS[type] || type}
            </span>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pt-3 pb-1">
        {/* Issue list */}
        <div className="space-y-1.5 mb-3">
          {(suggestion.issues || []).map((issue, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${ISSUE_TYPE_COLORS[issue.type] || ISSUE_TYPE_COLORS.other}`}>
                {ISSUE_TYPE_LABELS[issue.type] || issue.type}
              </span>
              <span className="text-slate-600">{issue.description}</span>
              {issue.original && (
                <>
                  <span className="text-slate-300 shrink-0">—</span>
                  <span className="line-through text-red-400 shrink-0">"{issue.original}"</span>
                  <span className="text-slate-300 shrink-0">→</span>
                  <span className="text-emerald-600 font-medium shrink-0">"{issue.suggestion}"</span>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Text comparison toggle */}
        <button
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mb-2"
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Metin karşılaştırmasını {expanded ? "gizle" : "göster"}
        </button>

        {expanded && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div className="rounded-lg border border-red-100 bg-red-50/50 p-3">
              <p className="text-[10px] font-semibold text-red-500 mb-1 uppercase tracking-wide">Orijinal</p>
              <p className="text-xs text-slate-700 leading-relaxed">{stripHtml(suggestion.original_text)}</p>
            </div>
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
              <p className="text-[10px] font-semibold text-emerald-600 mb-1 uppercase tracking-wide">Önerilen Düzeltme</p>
              <p className="text-xs text-slate-700 leading-relaxed">{stripHtml(suggestion.suggested_text)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 px-4 pb-3">
        <button
          onClick={() => onDismiss(suggestion.id)}
          disabled={dismissing || applying}
          data-testid="dismiss-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <X size={12} />
          Atla
        </button>
        <button
          onClick={() => onApply(suggestion.id)}
          disabled={applying || dismissing}
          data-testid="apply-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          <CheckCircle2 size={12} />
          {applying ? "Uygulanıyor..." : "Düzelt"}
        </button>
      </div>
    </div>
  );
}

// ── Analiz Paneli ─────────────────────────────────────────────────────────────

function AnalysisPanel({ onAnalyzed }) {
  const [lessons, setLessons] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [count, setCount] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!analyzing) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [analyzing]);

  useEffect(() => {
    api.get("/questions/meta/filters").then(r => {
      setLessons(r.data.lesson_names || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedLesson) { setTopics([]); setSelectedTopic(""); return; }
    api.get(`/taxonomy/children?parent_type=lesson_name&parent_value=${encodeURIComponent(selectedLesson)}`)
      .then(r => setTopics(r.data.children || []))
      .catch(() => {});
    setSelectedTopic("");
  }, [selectedLesson]);

  // Count matching questions
  useEffect(() => {
    const params = new URLSearchParams({ status: "active", limit: 1 });
    if (selectedLesson) params.set("lesson_name", selectedLesson);
    if (selectedTopic) params.set("topic", selectedTopic);
    api.get(`/questions?${params}`).then(r => setCount(r.data.total || 0)).catch(() => {});
  }, [selectedLesson, selectedTopic]);

  const pollJobStatus = async (jobId) => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const res = await api.get(`/quality/analyze/status/${jobId}`);
          const job = res.data;
          if (job.status === "done") {
            clearInterval(interval);
            const s = job.stats || {};
            setProgress({ done: true, analyzed: s.analyzed || 0, saved: s.saved || 0, issues: s.issues_found || 0 });
            onAnalyzed();
            resolve();
          } else if (job.status === "error") {
            clearInterval(interval);
            setProgress({ error: job.error || "Analiz başarısız oldu." });
            reject(new Error(job.error));
          }
          // status === "running" → devam et
        } catch (e) {
          clearInterval(interval);
          setProgress({ error: "Durum kontrolünde hata oluştu." });
          reject(e);
        }
      }, 2500);
    });
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setProgress({ running: true });
    try {
      const params = new URLSearchParams({ status: "active", limit: 10 });
      if (selectedLesson) params.set("lesson_name", selectedLesson);
      if (selectedTopic) params.set("topic", selectedTopic);
      const qRes = await api.get(`/questions?${params}`);
      const ids = (qRes.data.questions || []).map(q => q.id);
      if (!ids.length) { setProgress({ done: true, saved: 0, analyzed: 0, issues: 0 }); return; }

      // Arka planda başlatır, hemen job_id döner (timeout yok)
      const jobRes = await api.post("/quality/analyze", { question_ids: ids });
      const jobId = jobRes.data.job_id;
      if (!jobId) throw new Error("job_id alınamadı");

      setProgress({ running: true, jobId });
      await pollJobStatus(jobId);
    } catch (e) {
      if (!e.message?.includes("Analiz")) {
        setProgress({ error: "Analiz sırasında hata oluştu: " + (e.response?.data?.detail || e.message) });
      }
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white p-5 space-y-4" data-testid="analysis-panel">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-blue-500" />
        <h3 className="font-semibold text-slate-800 text-sm">Yeni Analiz Başlat</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Ders (opsiyonel)</label>
          <select
            value={selectedLesson}
            onChange={e => setSelectedLesson(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="">Tüm dersler</option>
            {lessons.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Konu (opsiyonel)</label>
          <select
            value={selectedTopic}
            onChange={e => setSelectedTopic(e.target.value)}
            disabled={!selectedLesson}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-50"
          >
            <option value="">Tüm konular</option>
            {topics.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Bu filtreden <span className="font-semibold text-slate-700">{count}</span> soru bulundu.
          İlk <span className="font-semibold text-slate-700">10</span> tanesi analiz edilecek.
        </p>
        <button
          onClick={handleAnalyze}
          disabled={analyzing || count === 0}
          data-testid="analyze-btn"
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <Sparkles size={14} className={analyzing ? "animate-pulse" : ""} />
          {analyzing ? "Analiz ediliyor..." : "Analiz Et"}
        </button>
      </div>

      {/* Progress */}
      {progress && (
        <div className={`rounded-lg px-4 py-3 text-sm ${
          progress.error ? "bg-red-50 border border-red-200 text-red-600" :
          progress.done ? "bg-emerald-50 border border-emerald-200 text-emerald-700" :
          "bg-blue-50 border border-blue-200 text-blue-700"
        }`}>
          {progress.error && progress.error}
          {progress.done && !progress.error && (
            <span>
              Analiz tamamlandı — <strong>{progress.analyzed}</strong> soru işlendi,{" "}
              <strong>{progress.issues}</strong> hata bulundu,{" "}
              <strong>{progress.saved}</strong> yeni öneri kaydedildi.
            </span>
          )}
          {progress.running && !progress.done && !progress.error && (
            <span className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              Sorular arka planda analiz ediliyor, lütfen bekleyin...
              {elapsed > 0 && <span className="text-blue-500 font-mono text-xs">({elapsed}s)</span>}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Ana Sayfa ─────────────────────────────────────────────────────────────────

export default function QualityControl() {
  const [suggestions, setSuggestions] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending"); // "pending" | "applied" | "dismissed"
  const [applying, setApplying] = useState(null);
  const [dismissing, setDismissing] = useState(null);
  const [filterLesson, setFilterLesson] = useState("");

  const loadSuggestions = useCallback(async (status = tab) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status });
      if (filterLesson) params.set("lesson_name", filterLesson);
      const res = await api.get(`/quality/suggestions?${params}`);
      setSuggestions(res.data.suggestions || []);
      setPendingCount(res.data.pending_count || 0);
    } finally {
      setLoading(false);
    }
  }, [tab, filterLesson]);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const handleApply = async (id) => {
    setApplying(id);
    try {
      await api.patch(`/quality/apply/${id}`);
      setSuggestions(prev => prev.filter(s => s.id !== id));
      setPendingCount(c => Math.max(0, c - 1));
    } catch { /* toast error */ }
    finally { setApplying(null); }
  };

  const handleDismiss = async (id) => {
    setDismissing(id);
    try {
      await api.delete(`/quality/suggestions/${id}`);
      setSuggestions(prev => prev.filter(s => s.id !== id));
      setPendingCount(c => Math.max(0, c - 1));
    } catch { }
    finally { setDismissing(null); }
  };

  const tabClass = (t) =>
    `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      tab === t
        ? "bg-white shadow-sm text-slate-800 border border-slate-200"
        : "text-slate-500 hover:text-slate-700"
    }`;

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto" data-testid="quality-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-600" />
            Kalite Kontrolü
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Gemini AI ile yazım, noktalama ve netlik hatalarını tespit et
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <span className="px-2.5 py-1 text-xs font-bold text-amber-700 bg-amber-100 rounded-full">
              {pendingCount} bekleyen öneri
            </span>
          )}
          <button
            onClick={() => loadSuggestions()}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Analysis Panel */}
      <AnalysisPanel onAnalyzed={() => { setTab("pending"); loadSuggestions("pending"); }} />

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        <button className={tabClass("pending")} onClick={() => setTab("pending")} data-testid="tab-pending">
          Bekleyen {pendingCount > 0 && <span className="ml-1 text-amber-600">({pendingCount})</span>}
        </button>
        <button className={tabClass("applied")} onClick={() => setTab("applied")} data-testid="tab-applied">
          Uygulananlar
        </button>
        <button className={tabClass("dismissed")} onClick={() => setTab("dismissed")} data-testid="tab-dismissed">
          Atlananlar
        </button>
      </div>

      {/* Suggestions list */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && suggestions.length === 0 && (
        <div className="text-center py-16 text-slate-400" data-testid="empty-state">
          <ShieldCheck size={40} className="mx-auto mb-3 text-slate-200" />
          <p className="text-sm font-medium text-slate-500">
            {tab === "pending" ? "Bekleyen öneri yok" : "Kayıt bulunamadı"}
          </p>
          {tab === "pending" && (
            <p className="text-xs text-slate-400 mt-1">
              Yukarıdaki "Analiz Et" ile soruları taratın
            </p>
          )}
        </div>
      )}

      {!loading && suggestions.length > 0 && (
        <div className="space-y-3" data-testid="suggestions-list">
          {suggestions.map(s => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              onApply={handleApply}
              onDismiss={handleDismiss}
              applying={applying === s.id}
              dismissing={dismissing === s.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
